<template>
  <div class="flex flex-col bg-white rounded-xl w-1/2">
    <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3 rounded-t-xl">
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-white">
          {{ $t('HeadContract.CostAggregationDashboard.ProjectWorksOverviewChart.title') }}
        </h2>

        <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
</svg>
        </div>
      </div>
    </div>

    <div class="p-4 mt-auto">
      <div class="md:h-80 relative">
        <Bar
          :data="chartData"
          :options="chartOptions"
          :chart-id="chartId"
          :dataset-id-key="datasetIdKey"
          :plugins="plugins"
          :css-classes="cssClasses"
          :styles="styles"
          :width="width"
          :height="height"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { t, tcc } from '@/i18n';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { ChartOptions } from 'chart.js';
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  costAggregationSummary: CostAggregationSummary;
}>();

// Chart configuration
const chartId = 'cost-aggregation-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => ({
  labels: [
    `${t('HeadContract.CostAggregationDashboard.ProjectWorksOverviewChart.totalWork')} ${
      tcc(props.costAggregationSummary.total_budget)
    }`,
    `${t('HeadContract.CostAggregationDashboard.ProjectWorksOverviewChart.workDone')} ${
      tcc(props.costAggregationSummary.total_certified)
    }`,
    `${t('HeadContract.CostAggregationDashboard.ProjectWorksOverviewChart.indirectCost')} ${
      tcc(props.costAggregationSummary.total_indirect_cost)
    }`,
    `${t('HeadContract.CostAggregationDashboard.ProjectWorksOverviewChart.approvedVariations')} ${
      tcc(props.costAggregationSummary.variations.approved_variations_total)
    }`,
  ],

  datasets: [{
    data: [
      props.costAggregationSummary.total_budget,
      props.costAggregationSummary.total_certified,
      props.costAggregationSummary.total_indirect_cost,
      props.costAggregationSummary.variations.approved_variations_total,
    ],

    backgroundColor: [
      'rgba(59, 130, 246, 0.8)',
      'rgba(99, 102, 241, 0.8)',
      'rgba(139, 92, 246, 0.8)',
      'rgba(168, 85, 247, 0.8)',
    ],

    borderColor: [
      'rgba(59, 130, 246, 1)',
      'rgba(99, 102, 241, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(168, 85, 247, 1)',
    ],

    borderWidth: 2,
    borderSkipped: false,
  }],
}));

const chartOptions = computed<ChartOptions<('bar')>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'white',
      bodyColor: 'white',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 6,
      displayColors: false,
      callbacks: {
        label(context: any) {
          return tcc(Number(context.parsed.y));
        },
      },
    },
  },

  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },

      ticks: {
        font: {
          size: 12,
        },

        color: '#6B7280',

        callback(value: any) {
          return tcc(Number(value));
        },
      },
    },

    x: {
      grid: {
        display: false,
      },

      ticks: {
        font: {
          size: 12,
        },

        color: '#6B7280',
        maxRotation: 45,
      },
    },
  },
}));
</script>

<style scoped></style>
